import React from 'react'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { cn } from '../UI/cn'

interface Ds2026SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  'aria-label'?: string
  disabled?: boolean
  /** Extra classes on the shell (e.g. a fixed width). */
  className?: string
}

/**
 * DS2026 search input — a white pill shell with a leading magnifier and a
 * borderless text field (Basil focus ring). Extracted from the Sequence
 * Templates drawer so the same search styling is reused across panels (e.g.
 * the Action Center's recently-completed panel).
 *
 * `!border` is required because an unlayered global bootstrap rule forces
 * border-color #dee2e6 and beats Tailwind's border-color utilities.
 */
export function Ds2026SearchInput({
  value,
  onChange,
  placeholder = 'Search',
  'aria-label': ariaLabel,
  disabled = false,
  className,
}: Ds2026SearchInputProps) {
  return (
    <div
      className={cn(
        'flex h-9 items-center rounded-lg border !border-neutral-300 px-3 transition-colors',
        disabled
          ? 'bg-surface-sunken'
          : 'bg-white hover:!border-neutral-400 focus-within:!border-brand-active focus-within:shadow-[0_0_0_2px_rgba(8,116,67,0.2)]',
        className
      )}
    >
      <PiMagnifyingGlass
        className={cn(
          'h-4 w-4 shrink-0',
          disabled ? 'text-neutral-300' : 'text-neutral-400'
        )}
      />
      <input
        type="text"
        aria-label={ariaLabel}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="ml-2 h-6 w-full border-none bg-transparent text-sm leading-5 text-neutral-800 outline-none placeholder:text-neutral-400 disabled:cursor-not-allowed"
      />
    </div>
  )
}
